/**
 * The API client.
 *
 * Everything is same-origin `/api/...`: in AWS the app and the API sit behind
 * one CloudFront distribution, and in development Vite proxies to the local
 * server. That keeps the session cookie SameSite=Lax and working.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  issues?: { field: string; message: string }[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }

  get issues() {
    return this.body.issues ?? [];
  }
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed = text === '' ? null : JSON.parse(text);

  if (response.status === 503) {
    // The whole site is off the air, so whichever screen made this call is
    // moot. Announce it once and let the app swap itself out, rather than
    // every page inventing its own way of failing.
    const body = (parsed as ApiErrorBody) ?? {
      code: 'maintenance',
      message: 'O Presente! está em manutenção. Volte daqui a pouco.',
    };
    window.dispatchEvent(
      new CustomEvent('presente:maintenance', { detail: body }),
    );
    throw new ApiError(503, body);
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (parsed as ApiErrorBody) ?? {
        code: 'unknown',
        message: 'Algo deu errado. Tente de novo.',
      },
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => call<T>('PATCH', path, body),
  del: <T>(path: string) => call<T>('DELETE', path),
};

/* ----------------------------------------------------------------- types */

export type RsvpState =
  | 'attending_staying'
  | 'attending_dropoff'
  | 'not_attending'
  | 'pending';

export interface PublicEvent {
  /** Uploaded invitation art, or null when the host has not added one. */
  artUrl: string | null;
  childName: string;
  ageTurning: number;
  startsAt: string;
  endsAt: string | null;
  timezone: string;
  venue: string;
  venueMapsUrl: string | null;
  rsvpDeadline: string;
  /** The deadline written out, e.g. "15 de setembro". */
  deadlineText: string;
  hostName: string;
  slug: string;
  whenText: string;
  /** A few lines from the host, shown to every family. */
  message: string | null;
}

export interface Person {
  personId: string;
  name: string;
  role: string;
  ageYears: number | null;
  rsvpState: RsvpState;
  addedByGuest: boolean;
}

export interface InvitationView {
  event: PublicEvent;
  household: {
    label: string;
    /** Written for this family. Never the host's private notes. */
    guestMessage: string | null;
    hasDietaryRestriction: boolean | null;
    dietaryNote: string | null;
    respondedAt: string | null;
  };
  people: Person[];
}

export interface HouseholdRow {
  householdId: string;
  label: string;
  source: string | null;
  contactName: string | null;
  notes?: string | null;
  guestMessage?: string | null;
  isHostFamily: boolean;
  sentAt: string | null;
  openedAt: string | null;
  respondedAt: string | null;
  hasDietaryRestriction: boolean | null;
  dietaryNote: string | null;
  invitationUrl: string;
  whatsappUrl: string;
  messagePreview: string;
  people: Person[];
}

export interface Counts {
  stayingAdults: number;
  stayingChildren: number;
  stayingChildrenByBand: Record<string, number>;
  dropoff: number;
  notAttending: number;
  pending: number;
  total: number;
}

export interface DashboardView {
  event: PublicEvent;
  counts: Counts;
  /** Whether this host may draw the invitation instead of uploading one. */
  canGenerateArt: boolean;
  pending: {
    householdId: string;
    label: string;
    sentAt: string | null;
    /** Set the first time somebody opened the link, crawlers excluded. */
    openedAt: string | null;
  }[];
}

/* ------------------------------------------------------------ reception */

export type Attendance = 'expected' | 'here' | 'left';

export interface ReceptionPerson {
  personId: string;
  householdId: string;
  name: string;
  role: string;
  ageYears: number | null;
  rsvpState: RsvpState;
  attendance: Attendance;
  arrivedAt: string | null;
  leftAt: string | null;
}

export interface ReceptionHousehold {
  householdId: string;
  label: string;
  contactName: string | null;
  hasDietaryRestriction: boolean | null;
  dietaryNote: string | null;
  people: ReceptionPerson[];
}

export interface ReceptionView {
  counts: {
    childrenHere: number;
    adultsHere: number;
    here: number;
    left: number;
    expected: number;
    arrivedTotal: number;
  };
  households: ReceptionHousehold[];
}

export interface ImportRow {
  sourceRow: number;
  label: string;
  source: string | null;
  contactName: string | null;
  contactRole: 'mother' | 'father' | null;
  anchorChildName: string | null;
  isHostFamily: boolean;
  /** Rows that look like the same family: same contact, another child. */
  mergeCandidates: number[];
  /** Rows the host joined into this one. */
  mergedRows: number[];
  /** Set on a row joined into another one. */
  mergedInto: number | null;
  status: 'novo' | 'atualizado' | 'duplicado' | 'juntado' | 'erro';
  issues: {
    code: string;
    severity: 'error' | 'warning';
    column: string | null;
    message: string;
  }[];
  people: { name: string; role: string; ageYears: number | null }[];
}

export interface ImportView {
  committed: boolean;
  counts: Record<string, number>;
  columnMap: { index: number; raw: string; key: string | null }[];
  unmappedHeaders: string[];
  ignoredHeaders: string[];
  households: ImportRow[];
}

/* ---------------------------------------------------------------- guest */

export const guest = {
  invitation: (token: string) =>
    api.get<InvitationView>(`/api/invite/${encodeURIComponent(token)}`),

  rsvp: (token: string, personId: string, state: RsvpState, ageYears?: number | null) =>
    api.post<{ person: Person }>(`/api/invite/${encodeURIComponent(token)}/rsvp`, {
      personId,
      state,
      ...(ageYears === undefined ? {} : { ageYears }),
    }),

  addPerson: (
    token: string,
    person: { name: string; role: string; ageYears?: number | null; state?: RsvpState },
  ) =>
    api.post<{ person: Person }>(
      `/api/invite/${encodeURIComponent(token)}/people`,
      person,
    ),

  dietary: (token: string, has: boolean | null, note?: string) =>
    api.post<void>(`/api/invite/${encodeURIComponent(token)}/dietary`, { has, note }),
};

/* ----------------------------------------------------------------- host */

export interface Me {
  signedIn: boolean;
  email?: string | null;
  eventId: string | null;
}

export const host = {
  me: () => api.get<Me>('/api/me'),

  createEvent: (input: Record<string, unknown>) =>
    api.post<{ eventId: string; slug: string }>('/api/events', input),

  requestLink: (email: string) =>
    api.post<void>('/api/auth/magic-link', { email }),

  consumeLink: (token: string) =>
    api.post<{ eventId: string | null }>(
      `/api/auth/session/${encodeURIComponent(token)}`,
    ),

  editEvent: (eventId: string, body: Record<string, unknown>) =>
    api.patch<{ message: string | null; event: PublicEvent }>(
      `/api/events/${encodeURIComponent(eventId)}`,
      body,
    ),

  dashboard: (eventId: string) =>
    api.get<DashboardView>(`/api/events/${encodeURIComponent(eventId)}`),

  reception: (eventId: string) =>
    api.get<ReceptionView>(`/api/events/${encodeURIComponent(eventId)}/recepcao`),

  checkIn: (
    eventId: string,
    householdId: string,
    personId: string,
    state: Attendance,
  ) =>
    api.post<{ person: ReceptionPerson }>(
      `/api/events/${encodeURIComponent(eventId)}/recepcao/${encodeURIComponent(
        householdId,
      )}/${encodeURIComponent(personId)}`,
      { state },
    ),

  checkInFamily: (eventId: string, householdId: string, state: Attendance) =>
    api.post<{ people: ReceptionPerson[] }>(
      `/api/events/${encodeURIComponent(eventId)}/recepcao/${encodeURIComponent(
        householdId,
      )}`,
      { state },
    ),

  /** Draw the invitation from a description. Nothing is stored until she saves. */
  generateArt: (eventId: string, prompt: string) =>
    api.post<{ image: string }>(
      `/api/events/${encodeURIComponent(eventId)}/arte/gerar`,
      { prompt },
    ),

  households: (eventId: string) =>
    api.get<{ households: HouseholdRow[] }>(
      `/api/events/${encodeURIComponent(eventId)}/households`,
    ),

  import: (
    eventId: string,
    options: {
      file: string;
      birthdayChildName?: string;
      labelOverrides?: Record<number, string>;
      /** Groups of rows the host said are one family, e.g. twins. */
      merges?: number[][];
      commit?: boolean;
    },
  ) =>
    api.post<ImportView>(
      `/api/events/${encodeURIComponent(eventId)}/import`,
      options,
    ),

  addHousehold: (
    eventId: string,
    body: {
      label: string;
      source?: string | null;
      contactName?: string | null;
      people?: { name: string; role: string; ageYears?: number | null }[];
    },
  ) =>
    api.post<{ household: HouseholdRow }>(
      `/api/events/${encodeURIComponent(eventId)}/households`,
      body,
    ),

  editHousehold: (
    eventId: string,
    householdId: string,
    body: Record<string, unknown>,
  ) =>
    api.patch<{ householdId: string; label: string }>(
      `/api/events/${encodeURIComponent(eventId)}/households/${encodeURIComponent(householdId)}`,
      body,
    ),

  removeHousehold: (eventId: string, householdId: string) =>
    api.del<void>(
      `/api/events/${encodeURIComponent(eventId)}/households/${encodeURIComponent(householdId)}`,
    ),

  addPerson: (
    eventId: string,
    householdId: string,
    body: { name: string; role: string; ageYears?: number | null },
  ) =>
    api.post<{ person: Person }>(
      `/api/events/${encodeURIComponent(eventId)}/households/${encodeURIComponent(householdId)}/people`,
      body,
    ),

  editPerson: (
    eventId: string,
    householdId: string,
    personId: string,
    body: Record<string, unknown>,
  ) =>
    api.patch<{ person: Person }>(
      `/api/events/${encodeURIComponent(eventId)}/households/${encodeURIComponent(householdId)}/people/${encodeURIComponent(personId)}`,
      body,
    ),

  removePerson: (eventId: string, householdId: string, personId: string) =>
    api.del<void>(
      `/api/events/${encodeURIComponent(eventId)}/households/${encodeURIComponent(householdId)}/people/${encodeURIComponent(personId)}`,
    ),

  markSent: (eventId: string, householdId: string) =>
    api.post<void>(
      `/api/events/${encodeURIComponent(eventId)}/households/${encodeURIComponent(householdId)}/sent`,
    ),
};
